import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, ButtonV2, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { ReactComponent as Warning } from '@assets/icons/Error-32.svg';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { majorToMinorUnits, minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import type { PayoutAccountInfo } from './PayoutDialog.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useMount } from '@shared/utils/hooks/useMount';
import { pxToRem } from '@withjoy/joykit/theme';
import { MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS } from './payoutFeeUtils';

interface AmountStepProps {
  readonly payoutAccountInfo: PayoutAccountInfo;
  readonly amountInMinorUnits: number;
  readonly onAmountChange: (amountInMinorUnits: number) => void;
  readonly onContinue: () => void;
  readonly onPageViewed?: () => void;
}

export const AmountStep: React.FC<AmountStepProps> = ({ payoutAccountInfo, amountInMinorUnits, onAmountChange, onContinue, onPageViewed }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatMinorUnitsCurrency, getCurrencySymbol } = useCurrencyFormatter();

  const currencyCode = payoutAccountInfo.balance?.available.currencyCode ?? payoutAccountInfo.currencyCode ?? DEFAULT_CURRENCY;
  const availableInMinorUnits = payoutAccountInfo.balance?.available.valueInMinorUnits ?? 0;

  const maxAmountInMinorUnits =
    payoutAccountInfo.payoutLimitInMinorUnits != null ? Math.min(payoutAccountInfo.payoutLimitInMinorUnits, availableInMinorUnits) : availableInMinorUnits;

  const minAmountInMinorUnits = MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS;

  const maxAmountInMajorUnits = minorToMajorUnits(maxAmountInMinorUnits, currencyCode);
  const minAmountInMajorUnits = minorToMajorUnits(MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS, currencyCode);

  const [rawInput, setRawInput] = useState<string>(() => String(minorToMajorUnits(amountInMinorUnits, currencyCode)));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useMount(() => {
    onPageViewed?.();
    inputRef.current?.focus();
    setIsFocused(true);
  });

  const isThrottled = useMemo(() => {
    if (!payoutAccountInfo.nextWithdrawalAvailableAt) return false;
    return payoutAccountInfo.nextWithdrawalAvailableAt.milliseconds > Date.now();
  }, [payoutAccountInfo.nextWithdrawalAvailableAt]);

  const parsedAmount = parseFloat(rawInput) || 0;
  const parsedAmountInMinorUnits = majorToMinorUnits(parsedAmount, currencyCode);
  const isAmountValid = parsedAmountInMinorUnits >= minAmountInMinorUnits && parsedAmountInMinorUnits <= maxAmountInMinorUnits;
  const isContinueDisabled = isThrottled || !isAmountValid;

  const newBalanceInMinorUnits = Math.max(0, availableInMinorUnits - parsedAmountInMinorUnits);
  const formattedNewBalance = formatMinorUnitsCurrency(newBalanceInMinorUnits, currencyCode);

  const formattedLimit = payoutAccountInfo.payoutLimitInMinorUnits != null ? formatMinorUnitsCurrency(payoutAccountInfo.payoutLimitInMinorUnits, currencyCode) : null;

  const throttleMessage = useMemo(() => {
    const nextAt = payoutAccountInfo.nextWithdrawalAvailableAt;
    if (!nextAt || !isThrottled) return null;
    const date = new Date(nextAt.milliseconds);
    return new Intl.DateTimeFormat(navigator.language, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'long',
    }).format(date);
  }, [payoutAccountInfo.nextWithdrawalAvailableAt, isThrottled]);

  const currencySymbol = getCurrencySymbol(currencyCode);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const cleaned = raw.replace(/[^0-9.]/g, '');
      const dotIndex = cleaned.indexOf('.');
      const normalized =
        dotIndex === -1
          ? cleaned
          : `${cleaned.slice(0, dotIndex)}.${cleaned
              .slice(dotIndex + 1)
              .replace(/\./g, '')
              .slice(0, 2)}`;
      setRawInput(normalized);
      const parsed = parseFloat(normalized) || 0;
      const inMinorUnits = majorToMinorUnits(parsed, currencyCode);
      const clamped = Math.max(minAmountInMinorUnits, Math.min(maxAmountInMinorUnits, inMinorUnits));
      onAmountChange(clamped);
    },
    [currencyCode, minAmountInMinorUnits, maxAmountInMinorUnits, onAmountChange],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const parsed = parseFloat(rawInput) || 0;
    const clamped = Math.max(minAmountInMajorUnits, Math.min(maxAmountInMajorUnits, parsed));
    const formatted = String(clamped);
    setRawInput(formatted);
    onAmountChange(majorToMinorUnits(clamped, currencyCode));
  }, [rawInput, minAmountInMajorUnits, maxAmountInMajorUnits, currencyCode, onAmountChange]);

  return (
    <>
      <Flex
        justifyContent="center"
        marginBottom={8}
      >
        <TextV2 typographyVariant="display1">{tq`Transfer to Bank`}</TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        alignItems="center"
        gap={4}
      >
        <TextV2
          typographyVariant="label1"
          color="mono10"
        >
          {tq`How much would you like to payout?`}
        </TextV2>
        <Flex
          borderStyle="solid"
          borderWidth="1px"
          borderColor={isFocused ? 'mono14' : 'mono3'}
          borderRadius={3}
          paddingX={6}
          paddingY={5}
          alignItems="center"
          justifyContent="center"
          width="100%"
          onClick={() => inputRef.current?.focus()}
          cursor="text"
        >
          <TextV2
            typographyVariant="display2"
            color="mono14"
            as="span"
          >
            {currencySymbol}
          </TextV2>
          <Box
            as="input"
            ref={inputRef}
            value={rawInput}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            color="mono14"
            border="none"
            outline="none"
            background="transparent"
            width="auto"
            minWidth="2ch"
            maxWidth="100%"
            textAlign="center"
            fontFamily="inherit"
            fontSize={pxToRem(36)}
            fontWeight="bold"
            lineHeight="1.2"
            letterSpacing="-0.015em"
          />
        </Flex>

        <TextV2
          typographyVariant="body1"
          color="mono10"
        >
          {tq`New Balance:`} {formattedNewBalance}
        </TextV2>

        {maxAmountInMinorUnits < MIN_PAYOUT_AMOUNT_IN_MINOR_UNITS && (
          <Flex
            alignItems="center"
            borderStyle="solid"
            borderWidth="1px"
            borderColor="negative3"
            borderRadius={3}
            backgroundColor="negative1"
            padding={5}
            width="100%"
            gap={4}
          >
            <IconV2
              size="lg"
              color="negative5"
            >
              <Warning />
            </IconV2>
            <TextV2
              typographyVariant="label2"
              color="mono14"
            >
              {tq`Sorry! A minimum balance of ${formatMinorUnitsCurrency(minAmountInMinorUnits, currencyCode)} is needed to process a balance transfer.`}
            </TextV2>
          </Flex>
        )}

        {formattedLimit !== null && (
          <TextV2
            typographyVariant="label1"
            color="negative6"
          >
            {tq`Limit per Withdrawal:`} {formattedLimit}
          </TextV2>
        )}

        {throttleMessage && (
          <TextV2
            typographyVariant="label1"
            color="negative6"
            textAlign="center"
          >
            {tq`Next withdrawal can be transferred at`} {throttleMessage}
          </TextV2>
        )}
      </Flex>

      <Flex
        justifyContent="center"
        marginTop={8}
      >
        <ButtonV2
          onClick={onContinue}
          size="lg"
          intent="neutral"
          shape="rounded"
          fullWidth
          disabled={isContinueDisabled}
        >
          {tq`Continue`}
        </ButtonV2>
      </Flex>
    </>
  );
};
